// lazyWithReload — self-healing wrapper around React.lazy for code-split routes.
//
// Problem: after a redeploy, an already-open tab still references the OLD
// hashed chunk filenames in its cached index.html. Navigating to a lazy route
// then dynamic-imports a chunk that 404s → the import rejects → the error
// surfaces as a generic "Something went wrong" boundary.
//
// Fix: when a lazy import fails AND the failure looks like a stale chunk /
// failed dynamic import, reload the page (the fresh index.html points at
// the current chunks). A sessionStorage COOLDOWN TIMESTAMP prevents an
// infinite reload loop while still allowing recovery from EVERY deploy:
// the original once-per-session boolean burned its single attempt on the
// first deploy, so a tab kept open across two deploys hit the boundary on
// the second ("Something went wrong" instead of self-healing). If a chunk
// still fails within the cooldown window after a reload, we rethrow so the
// GlobalErrorBoundary takes over. Genuine render/runtime errors are NOT
// dynamic-import failures, so they fall straight through to the boundary.

import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

const RELOAD_TS_KEY = 'tradeatory.chunkReloadAt';
// Long enough that a reload→still-404 loop can't spin (one reload per
// minute max), short enough that the next deploy hours later self-heals.
const RELOAD_COOLDOWN_MS = 60_000;

/** True when the error is a failed dynamic-import / stale code-split chunk. */
export function isChunkLoadError(err: unknown): boolean {
  const e = err as { name?: unknown; message?: unknown } | null | undefined;
  const name = typeof e?.name === 'string' ? e.name : '';
  const message = typeof e?.message === 'string' ? e.message : String(err ?? '');
  return (
    name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||      // Safari
    /Loading chunk [\d]+ failed/i.test(message) ||             // webpack-style
    /Loading CSS chunk/i.test(message)
  );
}

function withinCooldown(): boolean {
  try {
    const t = Number(sessionStorage.getItem(RELOAD_TS_KEY) ?? '0');
    return Number.isFinite(t) && Date.now() - t < RELOAD_COOLDOWN_MS;
  } catch { return false; }
}
function markReloaded(): void {
  try { sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now())); } catch { /* storage unavailable — skip */ }
}

/** Reload the page to pick up fresh chunk names, unless a reload already
 *  happened within the cooldown window (loop guard). Returns true when the
 *  reload was initiated. Shared by lazyWithReload and the global
 *  vite:preloadError handler. */
export function guardedStaleChunkReload(): boolean {
  if (typeof window === 'undefined' || withinCooldown()) return false;
  markReloaded();
  window.location.reload();
  return true;
}

/** Global safety net for chunk preload failures that don't flow through
 *  React.lazy (Vite's modulepreload of a chunk's static deps, CSS chunks).
 *  Vite dispatches 'vite:preloadError' on window; preventDefault() stops it
 *  from also throwing. Call once at app boot. */
export function installStaleChunkAutoReload(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('vite:preloadError', (event) => {
    if (guardedStaleChunkReload()) event.preventDefault();
    // Within cooldown → let it throw; the boundary shows the real error.
  });
}

/** Drop-in replacement for React.lazy that self-heals stale-chunk loads.
 *  Mirrors React.lazy's `ComponentType<any>` constraint so any route component
 *  (regardless of its props type) is accepted. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (err) {
      if (isChunkLoadError(err) && guardedStaleChunkReload()) {
        // Hold the Suspense fallback while the browser navigates away — never
        // resolve, so the error boundary doesn't flash before the reload.
        return new Promise<{ default: T }>(() => { /* intentionally pending */ });
      }
      // Reloaded too recently, or not a chunk error → let the boundary handle it.
      throw err;
    }
  });
}
